"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.removeIneligiblePayCodeSummaries = exports.getPayCodesEligibleForSelectedDate = exports.getPayCodesSummaryItemOnSelectedDate = exports.isSelectedDateSelfClassificationValid = exports.useGetSelfClassificationMessageBannerInfo = exports.useSelfClassification = void 0;
const isEmpty_1 = __importDefault(require("lodash/isEmpty"));
const luxon_1 = require("luxon");
const react_i18next_1 = require("react-i18next");
const hooks_1 = require("../../hooks");
const DEFAULT_PAY_CODE_POLICY_LANGUAGE = 'en-US';
const PAY_DATA_ITEM_INITIAL_REVISION_NUMBER = '0';
const useSelfClassification = ({ employeeId, selectedDate, timeZone, punchesTimeSegments, hourlyBreakdownProps, isSelfClassificationEnabled, isATPlusSelfClassificationEnabled, usageLocation }) => {
    const { data: { pay_period_breakdown_info: payPeriodBreakdownInfo } = {} } = (0, hooks_1.usePayPeriod)({
        employeeId,
        shouldFetch: isSelfClassificationEnabled || isATPlusSelfClassificationEnabled,
        usageLocation
    });
    const usePayCodePolicyResponse = (0, hooks_1.usePayCodePolicy)({
        employeeId,
        payPeriodBreakdownInfo,
        language: DEFAULT_PAY_CODE_POLICY_LANGUAGE,
        shouldFetch: isSelfClassificationEnabled,
        usageLocation
    });
    const usePayDataResponse = (0, hooks_1.usePayData)({
        employeeId,
        timeZone,
        payPeriodBreakdownInfo,
        shouldFetch: isSelfClassificationEnabled,
        usageLocation
    });
    const useUpdatePayDataResponse = (0, hooks_1.useUpdatePayData)({
        employeeId,
        timeZone,
        shouldFetch: isSelfClassificationEnabled,
        usageLocation
    });
    const usePayCodesResponse = (0, hooks_1.usePayCodes)({
        employeeId,
        payPeriodBreakdownInfo,
        shouldFetch: isATPlusSelfClassificationEnabled,
        usageLocation
    });
    const usePayCodesSummaryResponse = (0, hooks_1.usePayCodesSummary)({
        employeeId,
        payPeriodBreakdownInfo,
        timezone: timeZone,
        shouldFetch: isATPlusSelfClassificationEnabled,
        usageLocation
    });
    const useAddPayCodeResponse = (0, hooks_1.useAddPayCode)({
        employeeId,
        shouldFetch: isATPlusSelfClassificationEnabled,
        usageLocation
    });
    const useEditPayCodeResponse = (0, hooks_1.useEditPayCode)({
        employeeId,
        shouldFetch: isATPlusSelfClassificationEnabled,
        usageLocation
    });
    const messageBannerInfo = (0, exports.useGetSelfClassificationMessageBannerInfo)(punchesTimeSegments, usePayCodePolicyResponse, usePayDataResponse, useUpdatePayDataResponse, payPeriodBreakdownInfo, !!usePayCodesResponse.error, !!usePayCodesSummaryResponse.error, selectedDate, isSelfClassificationEnabled);
    if (!isSelfClassificationEnabled && !isATPlusSelfClassificationEnabled) {
        return hourlyBreakdownProps;
    }
    const isHourlyBreakdownLoading = !punchesTimeSegments ||
        !payPeriodBreakdownInfo ||
        !!usePayCodePolicyResponse.isPayCodePolicyLoading ||
        !!usePayDataResponse.isGetPayDataLoading ||
        !!usePayCodesResponse.isLoading ||
        !!usePayCodesSummaryResponse.isLoading;
    return {
        commonProps: Object.assign(Object.assign({}, hourlyBreakdownProps.commonProps), { isLoading: isHourlyBreakdownLoading }),
        selfClassificationProps: {
            payCodePolicyInfo: usePayCodePolicyResponse.payCodePolicyInfo,
            payCodesInfo: usePayCodesResponse.data,
            payPeriodBreakdownInfo,
            payDataItem: isSelfClassificationEnabled
                ? getPayDataItemOnSelectedDate(usePayDataResponse, selectedDate, timeZone)
                : undefined,
            payCodeSummary: isATPlusSelfClassificationEnabled
                ? (0, exports.getPayCodesSummaryItemOnSelectedDate)(usePayCodesSummaryResponse.data, selectedDate)
                : undefined,
            useUpdatePayDataResponse,
            useAddPayCodeResponse,
            useEditPayCodeResponse,
            employeeId,
            timeZone,
            messageBannerInfo
        }
    };
};
exports.useSelfClassification = useSelfClassification;
const useGetSelfClassificationMessageBannerInfo = (punchesTimeSegments, usePayCodePolicyResponse, usePayDataResponse, useUpdatePayDataResponse, payPeriodBreakdownInfo, usePayCodeResponseError, usePayCodeSummaryResponseError, selectedDateTime, isSelfClassificationEnabled) => {
    const { t } = (0, react_i18next_1.useTranslation)();
    if (!isSelfClassificationEnabled) {
        return undefined;
    }
    console.log(`Self Classification related API responses: \n
  GetPayCodePolicyResponse : ${JSON.stringify(usePayCodePolicyResponse)} \n
  GetPayDataResponse: ${JSON.stringify(usePayDataResponse)} \n
  UseUpdatePayDataResponse: ${JSON.stringify(useUpdatePayDataResponse)}`);
    if (!(0, exports.isSelectedDateSelfClassificationValid)(selectedDateTime, payPeriodBreakdownInfo)) {
        return {
            type: MessageBannerType.Informational,
            message: t(`time_and_attendance_HourlyBreakdown_SelfClassification_MessageBanner_OutOfPeriod`)
        };
    }
    if (hasMissedPunch(punchesTimeSegments)) {
        return {
            type: MessageBannerType.Warning,
            message: t(`time_and_attendance_HourlyBreakdown_SelfClassification_MessageBanner_MissedPunchError`)
        };
    }
    const hasBackendErrors = !!usePayCodePolicyResponse.errorMessage ||
        !!usePayDataResponse.errorMessage ||
        usePayCodeResponseError ||
        usePayCodeSummaryResponseError;
    if (hasBackendErrors) {
        return {
            type: MessageBannerType.Error,
            message: t(`time_and_attendance_HourlyBreakdown_SelfClassification_MessageBanner_BackendError`)
        };
    }
    return undefined;
};
exports.useGetSelfClassificationMessageBannerInfo = useGetSelfClassificationMessageBannerInfo;
const hasMissedPunch = (punchesTimeSegments) => {
    const missedPunchList = punchesTimeSegments === null || punchesTimeSegments === void 0 ? void 0 : punchesTimeSegments.filter((punchTimeSegment) => {
        return !punchTimeSegment.startDateTime || !punchTimeSegment.endDateTime;
    });
    return !(0, isEmpty_1.default)(missedPunchList);
};
const isSelectedDateSelfClassificationValid = (selectedDate, payPeriodBreakdownInfo) => {
    if (!payPeriodBreakdownInfo) {
        return false;
    }
    const { start_time, end_time } = payPeriodBreakdownInfo.current;
    const currentPayPeriodStartDT = luxon_1.DateTime.fromSQL(start_time.slice(0, 10));
    const currentPayPeriodEndDT = luxon_1.DateTime.fromSQL(end_time.slice(0, 10));
    const currentPayPeriodInterval = luxon_1.Interval.fromDateTimes(currentPayPeriodStartDT.plus({ days: 1 }), currentPayPeriodEndDT.plus({ days: 1 }));
    return currentPayPeriodInterval.contains(selectedDate);
};
exports.isSelectedDateSelfClassificationValid = isSelectedDateSelfClassificationValid;
const getPayDataItemOnSelectedDate = (usePayDataResponse, selectedDate, timeZone) => {
    var _a;
    return ((_a = usePayDataResponse.payDataItemsByDate[selectedDate.toISODate()]) !== null && _a !== void 0 ? _a : {
        payDate: selectedDate.toISODate(),
        payData: [],
        timezone: timeZone,
        revisionNumber: PAY_DATA_ITEM_INITIAL_REVISION_NUMBER
    });
};
const getPayCodesSummaryItemOnSelectedDate = (usePayCodesSummaryResponse, selectedDate) => {
    const selectedDateISO = selectedDate.toISODate();
    if (!!usePayCodesSummaryResponse) {
        for (const payCodeSummaryByDate of usePayCodesSummaryResponse.payCodeSummaryByDate) {
            if (payCodeSummaryByDate.date == selectedDateISO)
                return payCodeSummaryByDate;
        }
    }
    return {
        date: selectedDate.toISODate(),
        payCodeSummary: []
    };
};
exports.getPayCodesSummaryItemOnSelectedDate = getPayCodesSummaryItemOnSelectedDate;
const getPayCodesEligibleForSelectedDate = (usePayCodesResponse, selectedDate) => {
    return {
        payCodes: usePayCodesResponse === null || usePayCodesResponse === void 0 ? void 0 : usePayCodesResponse.payCodes.filter((paycode) => {
            const startPeriod = luxon_1.DateTime.fromISO(paycode.startDateTime);
            const endPeriod = luxon_1.DateTime.fromISO(paycode.endDateTime);
            if (startPeriod <= selectedDate && selectedDate <= endPeriod)
                return paycode;
        })
    };
};
exports.getPayCodesEligibleForSelectedDate = getPayCodesEligibleForSelectedDate;
const removeIneligiblePayCodeSummaries = (usePayCodesResponse, payCodeSummaryByDate) => {
    return payCodeSummaryByDate.payCodeSummary.length
        ? {
            date: payCodeSummaryByDate.date,
            payCodeSummary: payCodeSummaryByDate.payCodeSummary.filter((payCodeSummary) => {
                if (usePayCodesResponse.payCodes.find((paycode) => paycode.description === payCodeSummary.payCodeName))
                    return payCodeSummary;
            })
        }
        : payCodeSummaryByDate;
};
exports.removeIneligiblePayCodeSummaries = removeIneligiblePayCodeSummaries;
var MessageBannerType;
(function (MessageBannerType) {
    MessageBannerType["Informational"] = "informational";
    MessageBannerType["Warning"] = "warning";
    MessageBannerType["Error"] = "error";
})(MessageBannerType || (MessageBannerType = {}));
